import React, { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import CrmV2KanbanColumn from "./CrmV2KanbanColumn";
import { ODOO } from "./crmV2Helpers";

const FoldedStage = ({ stage, count, onUnfold }) => (
  <button
    type="button"
    onClick={onUnfold}
    title={stage.name}
    style={{
      width: 36,
      minWidth: 36,
      flexShrink: 0,
      alignSelf: "stretch",
      maxHeight: "calc(100vh - 220px)",
      background: "#e9ecef",
      border: `1px solid ${ODOO.border}`,
      borderRadius: 3,
      cursor: "pointer",
      writingMode: "vertical-rl",
      transform: "rotate(180deg)",
      fontSize: 12,
      fontWeight: 700,
      color: ODOO.text,
      letterSpacing: "0.04em",
      padding: "12px 6px",
      fontFamily: "inherit",
      textTransform: "uppercase",
    }}
  >
    {stage.name}{count != null ? ` (${count})` : ""}
  </button>
);

const StageSettingsModal = ({ stage, onClose, onSave, onDelete }) => {
  const [form, setForm] = useState(() => ({
    name: stage.name || "",
    probability: stage.probability ?? "",
    fold: !!stage.fold,
    is_active: stage.is_active !== false,
    is_won: !!stage.is_won,
    is_lost: !!stage.is_lost,
    auto_create_quotation: !!stage.auto_create_quotation,
  }));
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await onSave(stage.id, {
        ...form,
        name: form.name.trim(),
        probability: form.probability === "" ? undefined : Number(form.probability),
      });
      onClose();
    } catch {
      // Le parent affiche déjà le message d'erreur via le toast.
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the stage “${stage.name}”?`)) return;
    setBusy(true);
    try {
      await onDelete(stage.id);
      onClose();
    } catch {
      // Le parent affiche déjà le message d'erreur via le toast.
    } finally {
      setBusy(false);
    }
  };

  const field = { width: "100%", boxSizing: "border-box", border: `1px solid ${ODOO.border}`, borderRadius: 3, padding: "8px 10px", font: "inherit", fontSize: 13 };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(30, 41, 59, .32)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <form onSubmit={save} style={{ width: 460, maxWidth: "100%", background: "#fff", borderRadius: 5, boxShadow: "0 18px 45px rgba(0,0,0,.22)", overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${ODOO.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong style={{ fontSize: 15, color: ODOO.text }}>Stage settings</strong>
          <button type="button" onClick={onClose} aria-label="Close" style={{ border: "none", background: "transparent", cursor: "pointer", color: ODOO.muted, display: "inline-flex" }}><X size={18} /></button>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>Stage name<input autoFocus value={form.name} onChange={(e) => set("name", e.target.value)} style={{ ...field, marginTop: 5 }} /></label>
          <label style={{ fontSize: 12, fontWeight: 600, color: ODOO.text }}>Default probability (%)<input type="number" min="0" max="100" value={form.probability} onChange={(e) => set("probability", e.target.value)} style={{ ...field, marginTop: 5 }} /></label>
          <div style={{ display: "grid", gap: 9, paddingTop: 2 }}>
            {[
              ["fold", "Folded in Kanban"],
              ["is_active", "Visible in the pipeline"],
              ["is_won", "Won stage"],
              ["is_lost", "Lost stage"],
              ["auto_create_quotation", "Create quotation automatically"],
            ].map(([key, label]) => (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: ODOO.text, cursor: "pointer" }}>
                <input type="checkbox" checked={form[key]} onChange={(e) => {
                  const next = e.target.checked;
                  setForm((previous) => ({ ...previous, [key]: next, ...(key === "is_won" && next ? { is_lost: false } : {}), ...(key === "is_lost" && next ? { is_won: false } : {}) }));
                }} />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 16px", borderTop: `1px solid ${ODOO.border}`, background: "#fafbfc" }}>
          <button type="button" onClick={remove} disabled={busy} style={{ border: "none", background: "transparent", color: "#c0392b", cursor: busy ? "wait" : "pointer", display: "inline-flex", gap: 6, alignItems: "center", font: "inherit", fontSize: 13 }}><Trash2 size={15} /> Delete</button>
          <div style={{ display: "flex", gap: 8 }}><button type="button" onClick={onClose} disabled={busy} style={{ border: `1px solid ${ODOO.border}`, background: "#fff", borderRadius: 3, padding: "7px 12px", cursor: "pointer", font: "inherit" }}>Cancel</button><button type="submit" disabled={busy || !form.name.trim()} style={{ border: "none", background: ODOO.primary, color: "#fff", borderRadius: 3, padding: "7px 13px", cursor: "pointer", font: "inherit", fontWeight: 600 }}>{busy ? "Saving…" : "Save"}</button></div>
        </div>
      </form>
    </div>
  );
};

const CrmV2KanbanBoard = ({
  stages,
  columnState,
  foldedIds,
  canManage,
  draggingId,
  dragOverId,
  draggingStageId,
  dragOverStageId,
  userNameOf,
  onToggleFold,
  onAddInColumn,
  onLoadMore,
  onDragOver,
  onDragLeave,
  onDrop,
  onCardDragStart,
  onCardDragEnd,
  onStageDragStart,
  onStageDragEnd,
  onStageDragOver,
  onStageDragLeave,
  onStageDrop,
  onCardClick,
  onAddStage,
  addingStage,
  onUpdateStage,
  onDeleteStage,
  inlineCreateStageId,
  inlineBusy,
  onInlineAdd,
  onInlineEdit,
  onInlineCancel,
  onCardAction,
}) => {
  const [stageName, setStageName] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [settingsStage, setSettingsStage] = useState(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (settingsStage) {
        setSettingsStage(null);
        return;
      }
      setStageName("");
      setShowAdd(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [settingsStage]);

  const submitStage = async (e) => {
    e.preventDefault();
    if (!stageName.trim()) return;
    await onAddStage(stageName.trim());
    setStageName("");
    setShowAdd(false);
  };

  return (
    // NOTE: pas de overflowX ici. La largeur du board est laissee "naturelle"
    // (elle grandit avec le nombre de colonnes) et c'est le conteneur parent
    // dans AdminPanelCrmV2Page.js (celui avec overflow:"auto") qui gere la
    // scrollbar horizontale. Ca evite d'avoir deux scrollbars horizontales
    // qui se marchent dessus (une sur le board, une sur la page).
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        width: "max-content",
        background: ODOO.boardBg,
        minHeight: "calc(100vh - 260px)",
      }}
    >
      {stages.map((stage) => {
        const folded = foldedIds.has(String(stage.id));
        const st = columnState[stage.id] || { leads: [], total: 0, loading: false, loadingMore: false };

        if (folded) {
          // FoldedStage non draggable (choix briefing) — évite la rotation d'un bouton vertical 36px
          return (
            <FoldedStage
              key={stage.id}
              stage={stage}
              count={st.total}
              onUnfold={() => onToggleFold(stage.id, false)}
            />
          );
        }

        return (
          <CrmV2KanbanColumn
            key={stage.id}
            stage={stage}
            leads={st.leads}
            total={st.total}
            loading={st.loading}
            loadingMore={st.loadingMore}
            canManage={canManage}
            dragOver={dragOverId === stage.id}
            draggingId={draggingId}
            draggingStageId={draggingStageId}
            dragOverStageId={dragOverStageId}
            userNameOf={userNameOf}
            onAddInColumn={() => onAddInColumn(stage)}
            onLoadMore={() => onLoadMore(stage.id)}
            onDragOver={() => onDragOver(stage)}
            onDragLeave={() => onDragLeave(stage.id)}
            onDrop={() => onDrop(stage)}
            onStageDragStart={onStageDragStart}
            onStageDragEnd={onStageDragEnd}
            onStageDragOver={() => onStageDragOver(stage)}
            onStageDragLeave={() => onStageDragLeave(stage.id)}
            onStageDrop={() => onStageDrop(stage)}
            onCardDragStart={onCardDragStart}
            onCardDragEnd={onCardDragEnd}
            onCardClick={onCardClick}
            onFold={() => onToggleFold(stage.id, true)}
            onSettings={() => setSettingsStage(stage)}
            showInlineCreate={String(inlineCreateStageId) === String(stage.id)}
            inlineBusy={inlineBusy}
            onInlineAdd={onInlineAdd}
            onInlineEdit={onInlineEdit}
            onInlineCancel={onInlineCancel}
            onCardAction={onCardAction}
          />
        );
      })}

      {/* Colonne de création d'un stage */}
      {canManage && (
        <div style={{ width: 280, minWidth: 280, flexShrink: 0, alignSelf: "stretch", minHeight: "calc(100vh - 260px)", background: "#f7f9fa", borderLeft: `1px solid ${ODOO.border}`, padding: "14px 8px", boxSizing: "border-box" }}>
          {showAdd ? (
            <>
              <form onSubmit={submitStage} style={{ display: "flex", alignItems: "stretch", borderBottom: `1px solid ${ODOO.border}`, background: "#fff" }}>
              <input
                autoFocus
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                placeholder="Stage..."
                disabled={addingStage}
                style={{
                  flex: 1,
                  minWidth: 0,
                  boxSizing: "border-box",
                  border: "none",
                  padding: "8px 10px",
                  fontSize: 13,
                  fontFamily: "inherit",
                  outline: "none",
                }}
              />
              <button
                type="submit"
                disabled={addingStage || !stageName.trim()}
                style={{
                  background: ODOO.primary,
                  color: "#fff",
                  border: "none",
                  padding: "0 13px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: addingStage || !stageName.trim() ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {addingStage ? "…" : "ADD"}
              </button>
              </form>
              <div style={{ padding: "6px 4px 0", fontSize: 11, color: ODOO.muted }}>
                esc to discard
              </div>
              <div aria-hidden="true" style={{ display: "grid", gap: 12, padding: "24px 8px" }}>
                {[0, 1, 2].map((index) => (
                  <div key={index} style={{ height: 72, background: "#dde3e7", borderRadius: 1 }} />
                ))}
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              style={{ width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, border: `1px solid ${ODOO.border}`, background: "#fff", color: ODOO.muted, padding: "7px 10px", font: "inherit", fontSize: 13, cursor: "pointer" }}
            >
              <Plus size={14} strokeWidth={3} /> Stage
            </button>
          )}
        </div>
      )}

      {settingsStage && (
        <StageSettingsModal
          stage={settingsStage}
          onClose={() => setSettingsStage(null)}
          onSave={onUpdateStage}
          onDelete={onDeleteStage}
        />
      )}
    </div>
  );
};

export default CrmV2KanbanBoard;
